import { memo } from 'react';
import { Clock } from 'lucide-react';
import { Tooltip, KPI_TOOLTIPS } from '@/components/ui';
import type { StageVelocity } from '@/utils/analytics';
import type { Lead, Deal } from '@/types';
import type { AnalyticsDetailData } from './AnalyticsDetailModal';

interface StageVelocityCardProps {
  data: StageVelocity[];
  leads?: Lead[];
  deals?: Deal[];
  isLoading?: boolean;
  onStageClick?: (data: AnalyticsDetailData) => void;
}

export const StageVelocityCard = memo(function StageVelocityCard({
  data,
  leads = [],
  deals = [],
  isLoading,
  onStageClick,
}: StageVelocityCardProps) {
  const maxDays = Math.max(...data.map((d) => d.avgDays), 1);

  const handleStageClick = (item: StageVelocity) => {
    if (!onStageClick) return;

    // Check if this is a deal stage (prefixed with "Deal: ")
    const isDealStage = item.stage.startsWith('Deal: ');
    const actualStage = isDealStage ? item.stage.replace('Deal: ', '') : item.stage;

    if (isDealStage) {
      const stageDeals = deals.filter((d) => d.stage === actualStage);
      onStageClick({
        title: `${actualStage} Deals`,
        subtitle: `${item.avgDays} days average velocity`,
        deals: stageDeals,
      });
    } else {
      const stageLeads = leads.filter((l) => l.stage === item.stage);
      onStageClick({
        title: `${item.stage} Leads`,
        subtitle: `${item.avgDays} days average velocity`,
        leads: stageLeads,
      });
    }
  };

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="h-5 w-5 text-blue-400" />
        <Tooltip {...KPI_TOOLTIPS.stageVelocity}>
          <h3 className="text-lg font-semibold text-white">Stage Velocity</h3>
        </Tooltip>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Average days leads spend in each stage</p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-600 border-t-blue-400" />
        </div>
      ) : data.length === 0 ? (
        <p className="text-center text-zinc-500 py-4">No data available</p>
      ) : (
        <div className="space-y-3">
          {data.map((item) => (
            <button
              key={item.stage}
              onClick={() => handleStageClick(item)}
              className="w-full flex items-center gap-3 rounded-lg p-1 -m-1 hover:bg-zinc-800/50 transition-colors cursor-pointer text-left"
            >
              <span className="w-28 text-sm text-zinc-400 truncate" title={item.stage}>
                {item.stage}
              </span>
              <div className="flex-1 h-6 bg-zinc-800 rounded overflow-hidden relative">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded transition-all duration-500"
                  style={{ width: `${(item.avgDays / maxDays) * 100}%` }}
                />
                <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                  {item.avgDays}d
                </span>
              </div>
              <span className="w-12 text-xs text-zinc-500 text-right">
                {item.count} leads
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
